import { batch, bind, classNames, createRef, h, signal, tag } from "omi";
import isEmpty from "lodash-es/isEmpty";

import { fireEvent, ValidationEvents } from "../events.ts";
import { TextValidation } from "../models.ts";
import { ValidationStatus, ValidationType, FeatureKey } from "@/api";
import { NWButton, NWIcon, openProModal } from "@/wc/components";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";

import { EditAnswer } from "../common/editAnswer.tsx";
import baseStyles from "../baseStyles.css?inline";
import { ValidationBase } from "../validationBase.tsx";
import { NWCharacterCount } from "../../nw-character-count";

export type Props = {
  title: string;
  characterLimit: number;
  error?: string;
  description?: string;
  disabled?: boolean;
  optional?: boolean;
  /**
   * The placeholder text for the validation text, used to pre-fill the input.
   */
  placeholder?: string;
};

@tag("nw-validation-text")
export class NWValidationText extends ValidationBase<TextValidation, Props> {
  static css = [tailwind, baseStyles];

  static propTypes = {
    title: String,
    description: String,
    placeholder: String,
    error: String,
    disabled: Boolean,
    optional: Boolean,
    characterLimit: Number,
  };

  private textAreaRef = createRef<HTMLTextAreaElement>();
  private showAutosaveIndicator = signal(false);

  minimumToBeFilled = 3;
  text = signal<string>("");

  @bind
  isFilled(): boolean {
    return (
      this.text.value.trim().length >= this.minimumToBeFilled &&
      this.text.value.trim().length <= this.getCharacterLimit()
    );
  }

  @bind
  public async processInternal(): Promise<ValidationStatus> {
    const filled = this.isFilled();
    return filled ? ValidationStatus.Complete : ValidationStatus.Incomplete;
  }

  @bind
  hydrate(validation: TextValidation | undefined) {
    batch(() => {
      this.text.value = validation?.text?.data || this.text.value || this.props.placeholder || "";
      this.status.value = validation?.status || this.status.value;
    });
  }

  @bind
  toDTO(): TextValidation {
    return {
      id: this.id,
      status: this.status.value,
      type: ValidationType.Text,
      text: {
        data: this.text.value,
      },
    };
  }

  @bind
  private handleFocus() {
    if (isEmpty(this.text.value) && !isEmpty(this.props.placeholder)) {
      this.text.value = this.props.placeholder || "";
    }

    if (this.isOptional()) {
      fireEvent(ValidationEvents.OPTIONAL_FOCUSED, { id: this.id });
    }

    if (this.status.value === ValidationStatus.InReview) {
      this.status.value = ValidationStatus.Incomplete;
      fireEvent(ValidationEvents.IN_REVIEW_FOCUSED, { id: this.id });
    }

    if (Store.user.isPro() && this.status.value === ValidationStatus.Complete) {
      this.status.value = ValidationStatus.Incomplete;
      fireEvent(ValidationEvents.EDITED, { id: this.id });

      if (Store.project.id.value != null) {
        import("@/services/analyticsService").then(({ useAnalyticsService }) => {
          useAnalyticsService().trackValidationEdited(Store.project.id.value!, this.id);
        });
      }
    }
  }

  @bind
  private handleOnChange(event: Event) {
    const oldText = this.text.value;
    const eventValue = (event.target as HTMLInputElement).value;

    // Allow deletes by checking if the new value is shorter than the old value
    if (eventValue.length > this.getCharacterLimit() && eventValue.length > this.text.value.length) {
      return;
    }

    this.text.value = eventValue;

    // Handle clearing the validation when all data is removed.
    if (oldText.trim().length > this.minimumToBeFilled && eventValue.trim().length === 0) {
      this.save(true);
    }
  }

  @bind
  private getCharacterLimit() {
    return Store.features.getFeatureLimit(FeatureKey.VALIDATION_TEXT_CHARACTER);
  }

  @bind
  private handleShowPaywall() {
    openProModal("validation_text_limit");
  }

  @bind
  private renderSubtext() {
    const isCompleted = this.status.value === ValidationStatus.Complete;
    const hasError = !!this.props.error && this.props.error.length > 0;
    const hasReachedCharacterLimit = this.text.value.length >= this.getCharacterLimit();
    const isPro = Store.user.isPro();

    if (isCompleted) {
      return <EditAnswer onClick={this.focusTextArea} />;
    }

    if (hasError) {
      return this.props.error;
    }

    if (isPro) {
      return null;
    }

    return (
      <>
        {hasReachedCharacterLimit && (
          <NWIcon class="text-secondary mr-2" name="circle-info-filled" width={16} height={16} />
        )}
        <span class="text-secondary mr-2">
          {hasReachedCharacterLimit ? "Character limit reached" : "Need more space?"}
        </span>
        <NWButton type="text" size="fit" onClick={this.handleShowPaywall}>
          Upgrade to Pro
        </NWButton>
      </>
    );
  }

  @bind
  private focusTextArea() {
    this.textAreaRef.current?.focus();
  }

  render({ title, description, disabled = false, error = undefined }: Props) {
    const hasError = !!error && error.length > 0;
    const _disabled = disabled || !this.isEditable();
    const maxLength = Store.features.getFeatureLimit(FeatureKey.VALIDATION_TEXT_CHARACTER);
    const isCompleted = this.status.value === ValidationStatus.Complete;
    return (
      <div>
        <div className="space-y-4 mb-10">
          <h3 className="font-bold text-2xl text-primary">{title}</h3>
          {!isEmpty(description) && <p unsafeHTML={{ html: description }}></p>}
        </div>
        <div className="">
          <div class="relative">
            <textarea
              onFocus={this.handleFocus}
              ref={this.textAreaRef}
              disabled={_disabled}
              className={classNames(
                "w-full transition-colors duration-300 h-40 outline-none border px-3.5 py-3 rounded-lg bg-white shadow-xs m-0 placeholder:text-placeholder",
                {
                  "border-error-300 focus:shadow-xse focus:border-error-600": hasError,
                  "border-gray-300 focus:shadow-ring-sxs": !hasError,
                },
              )}
              placeholder={this.props.placeholder || "Go on, we know you have a great answer.."}
              maxLength={maxLength}
              onInputCapture={this.handleOnChange}
              value={this.text.value}
            />

            {!isCompleted && (
              <div class="absolute bottom-4 right-2.5">
                <NWCharacterCount count={this.text.value.length} limit={this.getCharacterLimit()} />
              </div>
            )}

            <div
              class={classNames(
                "absolute text-quaternary flex items-center space-x-1 bottom-2.5 right-2.5 p-1 transition-all duration-300 select-none pointer-events-none",
                {
                  "opacity-0": !this.showAutosaveIndicator.value,
                  "opacity-100": this.showAutosaveIndicator.value,
                },
              )}
            >
              <NWIcon name="save" width={12} height={12} />
              <p class="text-xs">Autosaved</p>
            </div>
          </div>

          <div>
            <div className="inline-flex items-center text-sm">{this.renderSubtext()}</div>
          </div>
        </div>
      </div>
    );
  }
}
